/**
 * What a tap on the world touched and what it says (decision 0023; visual spec, What a tap says).
 * The tile under a pixel is the rules' business and stays so. This looks along the same view ray
 * for what the player sees there, since a model stands in front of the tiles behind it: the crown
 * of a tall spruce may reach the ground five tiles further back. It changes nothing in the rules.
 *
 * Everything but the models' boxes and the mist is pure, so every renderer finds the same thing;
 * `spec/vectors/sayings.json` pins what a thing says.
 */
import { CATALOG, familyOf } from "@mistsweeper/assets";
import { mix32 } from "@mistsweeper/core";
import {
  bearsObjects,
  blockOn,
  creatureKind,
  HOME,
  type Pick,
  type Placement,
  SWAY,
  tileContent,
} from "./placement.ts";
import type { WorldSource } from "./world-source.ts";

/** A model's box in its own frame, in tiles: x, height, z. */
export interface Box {
  readonly min: readonly [number, number, number];
  readonly max: readonly [number, number, number];
}

/** What a touch needs to know of the world and the renderer. */
export interface TouchSource {
  readonly world: WorldSource;
  readonly pick: Pick;
  /** A model's box; null for a model the renderer lacks, which is not there to touch. */
  box(model: string): Box | null;
  /** Whether the mist has gone from a tile: on a tile still fading in, nothing stands yet. */
  clear(x: number, y: number): boolean;
  /** The player's own cabin, which stands where the rules stand the first (decision 0029). */
  readonly home: string;
}

/** A thing a touch found. */
export interface Thing {
  /** The tile it stands on: the lower corner of a block for a thing over four tiles. */
  readonly x: number;
  readonly y: number;
  readonly model: string;
  /** A creature standing where it hid, after the run's end. */
  readonly creature: boolean;
}

/** What a thing says: a string, its name for a house people live in, and an effect. */
export interface Saying {
  readonly key: string;
  readonly name?: string;
  /** The effect, without `effects.`. */
  readonly tap: string;
}

/** The view ray of a pixel: where it meets height 0, and the camera's yaw and pitch. */
export interface ViewRay {
  readonly px: number;
  readonly py: number;
  readonly yaw: number;
  readonly pitch: number;
}

const SALT_NAME = 0x4e414d45; // "NAME"
const SALT_SAY = 0x73617920; // "say "
/** How far beside the ray a thing's tile may lie: a block's thing reaches 1.5 tiles from its corner. */
const BESIDE = 2.5;

/**
 * What a thing says, or null when it has nothing to say (visual spec, What a tap says). The
 * cabin's tile says its own line whatever cabin stands there; a house people live in says who,
 * by the world, the same on every device; every other thing one of its lines, by its tile.
 */
export function sayingOf(model: string, x: number, y: number, seed: number): Saying | null {
  if (x === 0 && y === 0) return { key: "inspect.cabin", tap: "tap-home" };
  const def = familyOf(model);
  if (!def) return null;
  if (def.dwelling) {
    const names = CATALOG.names;
    const name = names[mix32(x, y, (seed ^ SALT_NAME) >>> 0) % names.length] as string;
    return { key: "inspect.home", name, tap: def.tap };
  }
  if (def.lines > 0) {
    const line = (mix32(x, y, (seed ^ SALT_SAY) >>> 0) % def.lines) + 1;
    return { key: `inspect.${def.name}.${line}`, tap: def.tap };
  }
  return def.category ? { key: `clearing.family.${def.name}`, tap: def.tap } : null;
}

/** What a creature says, by its model: the kind that hid there. */
export function creatureSaying(model: string): string {
  return `inspect.creature.${creatureKind(model)}`;
}

/** What a thing's model is on the screen: the player's own cabin where the rules stand the first. */
function shownModel(source: TouchSource, name: string): string {
  return name === HOME ? source.home : name;
}

/** Whether a thing on (x, y) can answer: it has a model here, and something to say. */
function speaks(source: TouchSource, name: string, x: number, y: number): boolean {
  return source.box(shownModel(source, name)) !== null && sayingOf(name, x, y, 0) !== null;
}

/**
 * Where the ray enters a model's box, seen from the camera: the parameter t of the point nearest
 * the camera, or null when it misses. In the model's own frame the ray stays a straight line with
 * the same t, so the box is cut along each of its axes in turn.
 */
export function rayEnters(
  box: Box,
  origin: readonly [number, number, number],
  way: readonly [number, number, number],
): number | null {
  let near = Number.NEGATIVE_INFINITY;
  let far = Number.POSITIVE_INFINITY;
  for (let axis = 0; axis < 3; axis += 1) {
    const o = origin[axis] as number;
    const d = way[axis] as number;
    const lo = box.min[axis] as number;
    const hi = box.max[axis] as number;
    if (Math.abs(d) < 1e-12) {
      if (o < lo || o > hi) return null;
      continue;
    }
    const a = (lo - o) / d;
    const b = (hi - o) / d;
    near = Math.max(near, Math.min(a, b));
    far = Math.min(far, Math.max(a, b));
  }
  return near <= far && far >= 0 ? far : null;
}

/** Where the ray enters a placement on tile (x, y): its box moved, turned and sized as drawn. */
function enters(box: Box, p: Placement, x: number, y: number, ray: ViewRay): number | null {
  const wayX = Math.cos(ray.pitch) * Math.sin(ray.yaw);
  const wayH = Math.sin(ray.pitch);
  const wayY = Math.cos(ray.pitch) * Math.cos(ray.yaw);
  const c = Math.cos(p.yaw);
  const s = Math.sin(p.yaw);
  const size = p.scale;
  const toModel = (rx: number, ry: number): [number, number] => [
    (rx * c - ry * s) / size,
    (rx * s + ry * c) / size,
  ];
  const [ox, oz] = toModel(ray.px - (x + p.dx), ray.py - (y + p.dy));
  const [wx, wz] = toModel(wayX, wayY);
  return rayEnters(box, [ox, -(p.h ?? 0) / size, oz], [wx, wayH / size, wz]);
}

/**
 * What stands on a tile, for a touch that has no finger or whose ray met nothing: its tallest
 * thing, or its block's on any of a block's four tiles, or, after the end, the creature in its gap.
 */
export function thingOn(source: TouchSource, x: number, y: number): Thing | null {
  const { world, pick } = source;
  const content = tileContent(world, x, y, pick);
  if (content?.kind === "gap" && content.creature) {
    return { x, y, model: content.creature.name, creature: true };
  }
  if (!source.clear(x, y)) return null;
  const block = blockOn(world, x, y, pick);
  const whole = block?.tiles.every((t) => bearsObjects(world, t.x, t.y)) ? block : null;
  const placements = whole
    ? [whole.placement]
    : content?.kind === "objects"
      ? content.placements
      : [];
  const at = whole ? { x: whole.x0, y: whole.y0 } : { x, y };
  let best: Thing | null = null;
  let tallest = Number.NEGATIVE_INFINITY;
  for (const p of placements) {
    const model = shownModel(source, p.name);
    const box = source.box(model);
    if (!box || !speaks(source, p.name, at.x, at.y)) continue;
    const top = (box.max[1] as number) * p.scale;
    if (top > tallest) {
      tallest = top;
      best = { ...at, model, creature: false };
    }
  }
  return best;
}

/**
 * What the ray of a pixel meets first on its way down from the camera, out to where it has risen
 * above `tallest`, or null when it meets nothing that has anything to say.
 */
export function thingAlong(source: TouchSource, ray: ViewRay, tallest: number): Thing | null {
  const { world, pick } = source;
  const reach = tallest / Math.tan(ray.pitch);
  const ux = Math.sin(ray.yaw);
  const uy = Math.cos(ray.yaw);
  const x0 = Math.round(Math.min(ray.px, ray.px + reach * ux) - BESIDE);
  const x1 = Math.round(Math.max(ray.px, ray.px + reach * ux) + BESIDE);
  const y0 = Math.round(Math.min(ray.py, ray.py + reach * uy) - BESIDE);
  const y1 = Math.round(Math.max(ray.py, ray.py + reach * uy) + BESIDE);
  let best: Thing | null = null;
  let first = Number.NEGATIVE_INFINITY;
  const offer = (thing: Thing, box: Box, p: Placement) => {
    const at = enters(box, p, thing.x, thing.y, ray);
    if (at !== null && at > first) {
      first = at;
      best = thing;
    }
  };
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      if (Math.abs((x - ray.px) * uy - (y - ray.py) * ux) > BESIDE) continue;
      const content = tileContent(world, x, y, pick);
      if (content?.kind === "gap" && content.creature) {
        const model = content.creature.name;
        const box = source.box(model);
        const spot = { name: model, dx: 0, dy: 0, yaw: 0, scale: 1, sway: SWAY.none };
        if (box) offer({ x, y, model, creature: true }, box, spot);
        continue;
      }
      if (content?.kind !== "objects" || !source.clear(x, y)) continue;
      for (const p of content.placements) {
        const model = shownModel(source, p.name);
        const box = source.box(model);
        if (box && speaks(source, p.name, x, y)) {
          offer({ x, y, model, creature: false }, box, p);
        }
      }
    }
  }
  return best;
}

/** The whole answer to a touch: what the ray meets, else what stands on the tile under it. */
export function thingAt(source: TouchSource, ray: ViewRay, tallest: number): Thing | null {
  return (
    thingAlong(source, ray, tallest) ?? thingOn(source, Math.round(ray.px), Math.round(ray.py))
  );
}
